import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Hash, User, Lock, LogIn, Leaf } from 'lucide-react';
import useAuth from '../../hooks/useAuth';

export default function StudentLogin() {
  const { studentLogin, isLoading, error, clearError } = useAuth();
  const [formData, setFormData] = useState({
    classCode: '',
    name: '',
    pin: ['', '', '', ''],
  });
  const pinRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    clearError();
  };

  const handlePinChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...formData.pin];
    newPin[index] = value;
    setFormData({ ...formData, pin: newPin });
    clearError();

    // Auto-focus next input
    if (value && index < 3) {
      pinRefs[index + 1].current?.focus();
    }
  };

  const handlePinKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !formData.pin[index] && index > 0) {
      pinRefs[index - 1].current?.focus();
    }
  };

  const handlePinPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    if (/^\d+$/.test(pastedData)) {
      const newPin = [...formData.pin];
      for (let i = 0; i < pastedData.length; i++) {
        newPin[i] = pastedData[i];
      }
      setFormData({ ...formData, pin: newPin });
      // Focus the next empty input or the last one
      const nextIndex = Math.min(pastedData.length, 3);
      pinRefs[nextIndex].current?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const pin = formData.pin.join('');
    if (pin.length !== 4) {
      return;
    }
    await studentLogin(formData.classCode, formData.name, pin);
  };

  const isPinComplete = formData.pin.every(digit => digit !== '');

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Card */}
        <div className="card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-leaf-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Leaf className="text-leaf-600" size={32} />
            </div>
            <h1 className="text-2xl mb-2">Student Sign In</h1>
            <p className="text-gray-600">Enter your class code and PIN to continue</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Class Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class Code
              </label>
              <div className="relative">
                <Hash className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  name="classCode"
                  value={formData.classCode}
                  onChange={handleChange}
                  required
                  placeholder="Enter 6-character code"
                  maxLength={6}
                  className="input-field pl-12 uppercase tracking-widest font-mono"
                  style={{ letterSpacing: '0.2em' }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Ask your teacher for the class code
              </p>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Name
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your full name"
                  className="input-field pl-12"
                />
              </div>
            </div>

            {/* PIN */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your PIN
              </label>
              <div className="flex justify-center gap-3">
                {formData.pin.map((digit, index) => (
                  <input
                    key={index}
                    ref={pinRefs[index]}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handlePinChange(index, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(index, e)}
                    onPaste={index === 0 ? handlePinPaste : undefined}
                    className="w-14 h-14 text-center text-2xl font-bold rounded-xl border-2
                             border-gray-200 focus:border-leaf-500 focus:ring-2 focus:ring-leaf-200
                             transition-all"
                    required
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Your teacher gave you a 4-digit PIN
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading || !isPinComplete}
              className="btn-primary w-full flex items-center justify-center gap-2 mt-6"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn size={20} />
                  Start Learning
                </>
              )}
            </button>
          </form>

          {/* Teacher Login Link */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-center text-sm text-gray-500 mb-3">Are you a teacher?</p>
            <Link
              to="/login"
              className="block w-full py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-700
                       font-medium text-center hover:bg-gray-50 transition-colors"
            >
              Teacher Sign In
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
